import { ArrowLeft, CheckCircle2, ChevronRight, Download, Loader2, XCircle } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { Link, useParams } from 'react-router-dom';
import SurveyResponseForm from '../components/pages-specific/SurveyResponseForm';
import { Button } from '../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import useLocale from '../contexts/LocaleContext';
import { getCurrentUser } from '../hooks/useAuth';
import { useSurveyResponse, useSurveyTracking } from '../hooks/useSurveys';
import { handleApiError, surveysApi } from '../lib/api-client';
import { hasPermission } from '../lib/permissions';
import type { SurveyDto } from '../types/api';

interface Crumb {
  id: string;
  name: string;
}

const localizedTitle = (survey: Pick<SurveyDto, 'title' | 'titleKinyarwanda' | 'titleFrench'>, locale: string) =>
  locale === 'rw'
    ? (survey as SurveyDto).titleKinyarwanda || survey.title
    : locale === 'fr'
      ? (survey as SurveyDto).titleFrench || survey.title
      : survey.title;

export default function SurveyDetail() {
  const { id: surveyId } = useParams<{ id: string }>();
  const { locale, dictionary } = useLocale();
  const currentUser = getCurrentUser();

  const [breadcrumb, setBreadcrumb] = useState<Crumb[]>([]);
  const [selectedLocation, setSelectedLocation] = useState<{ id: string; name: string } | null>(null);
  const [isExporting, setIsExporting] = useState(false);

  const currentLocationId = breadcrumb.length > 0 ? breadcrumb[breadcrumb.length - 1].id : undefined;
  const { data: tracking, isLoading: trackingLoading } = useSurveyTracking(surveyId, currentLocationId);
  const { data: responseData, isLoading: responseLoading } = useSurveyResponse(surveyId, selectedLocation?.id);

  const canRespond = hasPermission(currentUser, 'SURVEYS_RESPOND');
  const surveyOpen = tracking?.survey.status === 'ACTIVE';

  // Respondent-level users (e.g. a village admin on a village survey) land straight on their form
  useEffect(() => {
    if (!tracking || selectedLocation || breadcrumb.length > 0) return;
    if (tracking.rows.length === 1 && tracking.rows[0].isLeaf) {
      const only = tracking.rows[0].location;
      setSelectedLocation({ id: only.id, name: only.name });
    }
  }, [tracking, selectedLocation, breadcrumb.length]);

  const totalsPercent = useMemo(() => {
    if (!tracking || tracking.totals.expected === 0) return 0;
    return Math.round((tracking.totals.responded / tracking.totals.expected) * 100);
  }, [tracking]);

  const handleDrill = (row: { location: { id: string; name: string }; isLeaf: boolean }) => {
    if (row.isLeaf) {
      setSelectedLocation({ id: row.location.id, name: row.location.name });
    } else {
      setBreadcrumb(prev => [...prev, { id: row.location.id, name: row.location.name }]);
    }
  };

  const handleExport = async () => {
    if (!surveyId) return;
    setIsExporting(true);
    try {
      await surveysApi.exportExcel(surveyId, currentLocationId);
    } catch (error) {
      handleApiError(error);
      toast.error(dictionary['surveys.export_failed'] || 'Failed to download report');
    } finally {
      setIsExporting(false);
    }
  };

  if (trackingLoading && !tracking) {
    return (
      <div className='p-6 flex items-center justify-center gap-2 text-sm text-gray-500'>
        <Loader2 className='h-4 w-4 animate-spin' />
        {dictionary['common.loading'] || 'Loading...'}
      </div>
    );
  }

  if (!tracking) return null;

  return (
    <div className='p-3 sm:p-6 space-y-4'>
      {/* Header */}
      <div className='flex flex-wrap items-start justify-between gap-2'>
        <div>
          <Link to='/surveys' className='inline-flex items-center gap-1 text-xs text-gray-500 hover:text-primary mb-1'>
            <ArrowLeft className='h-3.5 w-3.5' /> {dictionary['surveys.back_to_surveys'] || 'All surveys'}
          </Link>
          <h1 className='text-lg font-semibold text-gray-900'>{localizedTitle(tracking.survey, locale)}</h1>
          <p className='text-xs text-gray-500 mt-0.5'>
            {dictionary['surveys.answered_at_level'] || 'Answered at'}{' '}
            <span className='font-medium'>{tracking.survey.respondentLocationType}</span>
            {!surveyOpen && (
              <span className='ml-2 text-gray-400'>({dictionary['surveys.status_closed'] || 'Closed'})</span>
            )}
          </p>
        </div>
        <Button type='button' variant='outline' size='sm' onClick={handleExport} disabled={isExporting} className='text-xs'>
          {isExporting ? <Loader2 className='h-3.5 w-3.5 mr-1 animate-spin' /> : <Download className='h-3.5 w-3.5 mr-1' />}
          {dictionary['surveys.export_report'] || 'Export report'}
        </Button>
      </div>

      {/* Completion summary */}
      <Card>
        <CardContent className='p-4'>
          <div className='flex flex-wrap items-center justify-between gap-3'>
            <div className='flex gap-5 text-sm'>
              <div>
                <p className='text-xs text-gray-500'>{dictionary['surveys.expected'] || 'Expected'}</p>
                <p className='font-semibold text-gray-900'>{tracking.totals.expected}</p>
              </div>
              <div>
                <p className='text-xs text-gray-500'>{dictionary['surveys.responded'] || 'Responded'}</p>
                <p className='font-semibold text-green-600'>{tracking.totals.responded}</p>
              </div>
              <div>
                <p className='text-xs text-gray-500'>{dictionary['surveys.pending'] || 'Pending'}</p>
                <p className='font-semibold text-red-500'>{tracking.totals.pending}</p>
              </div>
            </div>
            <div className='flex items-center gap-2 min-w-[160px] flex-1 max-w-xs'>
              <div className='flex-1 bg-gray-100 rounded-full h-2'>
                <div className='bg-primary h-2 rounded-full transition-all' style={{ width: `${totalsPercent}%` }} />
              </div>
              <span className='text-sm font-semibold text-gray-700'>{totalsPercent}%</span>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className='grid grid-cols-1 lg:grid-cols-2 gap-4'>
        {/* Tracking drill-down */}
        <Card>
          <CardHeader className='pb-2'>
            <CardTitle className='text-sm'>{dictionary['surveys.tracking_title'] || 'Response tracking'}</CardTitle>
            {/* Breadcrumb */}
            <div className='flex flex-wrap items-center gap-1 text-xs text-gray-500'>
              <button
                type='button'
                className='hover:text-primary font-medium'
                onClick={() => {
                  setBreadcrumb([]);
                  setSelectedLocation(null);
                }}
              >
                {dictionary['surveys.breadcrumb_root'] || 'My area'}
              </button>
              {breadcrumb.map((crumb, index) => (
                <span key={crumb.id} className='flex items-center gap-1'>
                  <ChevronRight className='h-3 w-3' />
                  <button
                    type='button'
                    className='hover:text-primary'
                    onClick={() => setBreadcrumb(prev => prev.slice(0, index + 1))}
                  >
                    {crumb.name}
                  </button>
                </span>
              ))}
            </div>
          </CardHeader>
          <CardContent className='pt-1'>
            {trackingLoading ? (
              <div className='py-6 flex justify-center'>
                <Loader2 className='h-4 w-4 animate-spin text-gray-400' />
              </div>
            ) : tracking.rows.length === 0 ? (
              <p className='py-6 text-center text-xs text-gray-400 italic'>
                {dictionary['surveys.no_locations'] || 'No expected respondents under this location.'}
              </p>
            ) : (
              <div className='divide-y divide-gray-100'>
                {tracking.rows.map(row => (
                  <button
                    key={row.location.id}
                    type='button'
                    onClick={() => handleDrill(row)}
                    className={`w-full flex items-center justify-between gap-2 py-2.5 px-2 text-left hover:bg-gray-50 rounded transition-colors ${
                      selectedLocation?.id === row.location.id ? 'bg-secondary' : ''
                    }`}
                  >
                    <div className='min-w-0'>
                      <p className='text-sm font-medium text-gray-900 truncate'>{row.location.name}</p>
                      <p className='text-xs text-gray-500'>
                        {row.isLeaf ? (
                          row.response ? (
                            <>
                              {dictionary['surveys.responded_by'] || 'Responded by'}{' '}
                              <span className='font-medium'>{row.response.submittedByName || '—'}</span> ·{' '}
                              {new Date(row.response.submittedAt).toLocaleDateString()}
                            </>
                          ) : (
                            dictionary['surveys.not_responded'] || 'Not responded yet'
                          )
                        ) : (
                          <>
                            {row.responded}/{row.expected} {dictionary['surveys.responded'] || 'responded'}
                          </>
                        )}
                      </p>
                    </div>
                    <div className='flex items-center gap-2 shrink-0'>
                      {row.isLeaf ? (
                        row.response ? (
                          <CheckCircle2 className='h-4 w-4 text-green-500' />
                        ) : (
                          <XCircle className='h-4 w-4 text-red-400' />
                        )
                      ) : (
                        <>
                          <span
                            className={`text-xs font-semibold ${
                              row.pending === 0 ? 'text-green-600' : 'text-gray-600'
                            }`}
                          >
                            {row.expected > 0 ? Math.round((row.responded / row.expected) * 100) : 0}%
                          </span>
                          <ChevronRight className='h-4 w-4 text-gray-300' />
                        </>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Response panel */}
        <Card>
          <CardHeader className='pb-2'>
            <CardTitle className='text-sm'>
              {selectedLocation
                ? `${dictionary['surveys.response_for'] || 'Response for'} ${selectedLocation.name}`
                : dictionary['surveys.select_location'] || 'Select a location'}
            </CardTitle>
          </CardHeader>
          <CardContent className='pt-1'>
            {!selectedLocation ? (
              <p className='py-8 text-center text-xs text-gray-400 italic'>
                {dictionary['surveys.select_location_hint'] ||
                  'Pick a location in the tracking list to view or fill in its response.'}
              </p>
            ) : responseLoading ? (
              <div className='py-8 flex justify-center'>
                <Loader2 className='h-4 w-4 animate-spin text-gray-400' />
              </div>
            ) : responseData ? (
              <SurveyResponseForm
                survey={responseData.survey}
                location={responseData.location}
                response={responseData.response}
                readOnly={!canRespond || !surveyOpen}
              />
            ) : null}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
